import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Button,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

// CONSTANTS
import { FundsPaymentType } from '@/constants/streamingRewards';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';

// HOC
import withStripeElements from '@/hoc/withStripeElements';

// COMPONENTS
import AlertError from '@/components/Alerts/AlertError';

// HELP
import CreditCard, { CardMode } from './help/CreditCard';

// STYLES
import { StyledAmountField, StyledFundsForm, StyledTransMethodField } from './styles';

function FundsForm({
  variant,
  defaultCard,
  payFunds,
  stripe,
  elements,
  errorMessage,
  onSuccessCallback,
  hideMonthlyPayment,
  initialValues,
}) {
  const [t] = useTranslation();
  const navigate = useNavigate();

  const showError = !!errorMessage;
  const isDialog = variant === 'dialog';

  function getAmountAdornment(type) {
    const currency = 'USD';

    return type === FundsPaymentType.MONTHLY ? `${currency}/${t('common.month')}` : currency;
  }

  function formatAmount(value) {
    const amount =
      value &&
      value
        .replace(/[^\d.]|^\./g, '')
        .replace(/(\.+.*)\.$/g, (_, m1) => m1)
        .replace(/(\.\d{2})\d+$/g, (_, m1) => m1);

    return amount;
  }

  function onSubmit(values, { resetForm }) {
    const { type, amount, save, cardMode } = values;
    const isFormMode = cardMode === CardMode.form;

    const paymentData = {
      type,
      amount,
      save: isFormMode ? save : false,
      payment_method_id: isFormMode ? null : defaultCard?.payment_method_id,
    };

    if (!stripe || !elements) {
      return;
    }

    function confirmCardPayment(client_secret) {
      return stripe.confirmCardPayment(client_secret).then(({ error, paymentIntent }) => {
        if (error) {
          throw error;
        }

        return paymentIntent;
      });
    }

    function callback(data) {
      onSuccessCallback(data);

      resetForm();
    }

    if (paymentData.payment_method_id) {
      payFunds({ paymentData, confirmCardPayment, callback });

      return;
    }

    const card = elements.getElement('cardNumber');

    function createPaymentMethod() {
      return stripe
        .createPaymentMethod({
          card,
          type: 'card',
          billing_details: {
            address: {
              postal_code: values.zipcode,
            },
          },
        })
        .then(({ error, paymentMethod }) => {
          if (error) {
            throw error;
          }

          return paymentMethod;
        });
    }

    payFunds({
      paymentData,
      createPaymentMethod,
      confirmCardPayment,
      callback,
    });
  }

  return (
    <Formik
      initialValues={{
        save: true,
        type: FundsPaymentType.ONE_TIME,
        defaultCard,
        cardMode: defaultCard ? CardMode.placeholder : CardMode.form,
        ...initialValues,
      }}
      validationSchema={setYupValidationSchema('fundsPaymentSchema', t)}
      onSubmit={onSubmit}
      enableReinitialize
      initialTouched={{
        amount: !!initialValues.amount,
      }}
      validateOnMount
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
      }) => (
        <Form>
          <StyledFundsForm className={clsx('funds-form', isDialog && 'funds-form--dialog')}>
            <StyledAmountField className="amount-field">
              <InputLabel className="p4"> {t('inputs.addAmount')}</InputLabel>
              <TextField
                name="amount"
                value={values.amount}
                onChange={({ target: { name, value } }) => {
                  const amount = formatAmount(value);

                  setFieldValue(name, amount, true);
                }}
                onBlur={handleBlur}
                error={touched.amount && !!errors.amount}
                helperText={touched.amount && errors.amount}
                className="amount"
                type="text"
                size="small"
                inputMode="decimal"
                variant="outlined"
                placeholder="Amount"
                inputProps={{
                  maxLength: 7,
                }}
                InputProps={{
                  endAdornment: (
                    <Typography className="p5">{getAmountAdornment(values.type)}</Typography>
                  ),
                }}
              />
            </StyledAmountField>
            <StyledTransMethodField
              component="fieldset"
              className={clsx('type-field', !isDialog && 'type-field--large')}
            >
              <FormLabel className="p4 type-field__label">
                {t('streamingRewardsFunds.transMethod.label')}
              </FormLabel>
              <RadioGroup
                className="type"
                name="type"
                color="primary"
                value={values.type}
                onChange={handleChange}
              >
                <FormControlLabel
                  className={clsx(
                    'type__label',
                    values.type === FundsPaymentType.ONE_TIME && 'type__label--checked',
                  )}
                  value={FundsPaymentType.ONE_TIME}
                  color="primary"
                  control={<Radio color="primary" />}
                  label={t('streamingRewardsFunds.transMethod.oneTimePayment')}
                />
                {!hideMonthlyPayment && (
                  <FormControlLabel
                    className={clsx(
                      'type__label',
                      values.type === FundsPaymentType.MONTHLY && 'type__label--checked',
                    )}
                    value={FundsPaymentType.MONTHLY}
                    color="primary"
                    control={<Radio color="primary" />}
                    label={
                      <>
                        {t('streamingRewardsFunds.transMethod.montlyPayments')}
                        <Tooltip
                          title={
                            <p className="p5">
                              {t('streamingRewardsFunds.transMethod.montlyPaymentsInfo')}
                            </p>
                          }
                          placement="bottom-end"
                        >
                          <InfoIcon className="info-icon" />
                        </Tooltip>
                      </>
                    }
                  />
                )}
              </RadioGroup>
            </StyledTransMethodField>
            <CreditCard
              defaultCard={defaultCard}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              values={values}
              touched={touched}
              errors={errors}
            />
            <AlertError message={errorMessage} open={showError} />
            <div className="funds-form__buttons">
              {!isDialog && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  className="funds-form__back"
                  onClick={() => navigate(-1)}
                >
                  {t('buttons.back')}
                </Button>
              )}
              <Button
                type="submit"
                disabled={!isValid && !isSubmitting}
                className="funds-form__submit"
                size="small"
                variant="contained"
                color="primary"
              >
                {t('buttons.addFunds')}
              </Button>
            </div>
          </StyledFundsForm>
        </Form>
      )}
    </Formik>
  );
}

FundsForm.defaultProps = {
  onSuccessCallback: () => {},
  hideMonthlyPayment: false,
  initialValues: {},
  variant: 'default', // dialog
};

FundsForm.propTypes = {
  payFunds: PropTypes.func.isRequired,
  variant: PropTypes.string,
  stripe: PropTypes.object,
  elements: PropTypes.object,
  defaultCard: PropTypes.object,
  initialValues: PropTypes.object,
  errorMessage: PropTypes.string,
  hideMonthlyPayment: PropTypes.bool,
  onSuccessCallback: PropTypes.func,
};

export default withStripeElements(FundsForm);
